import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  LogOut, Calendar, DollarSign, Users, 
  ChevronDown, MoreVertical, Eye, Trash2, CheckCircle,
  XCircle, RefreshCw, Plus, X, UserPlus, Mail, ShoppingCart,
  CalendarX, UserCheck, FileText, Send, Edit2
} from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "../components/ui/dropdown-menu";
import { Calendar as CalendarComponent } from "../components/ui/calendar";
import { toast } from "sonner";
import axios from "axios";
import { format } from "date-fns";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [refreshing, setRefreshing] = useState(false);
  const [activeTab, setActiveTab] = useState("bookings");
  
  // Calendar/Dates state
  const [unavailableDates, setUnavailableDates] = useState([]);
  const [staffAssignments, setStaffAssignments] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [showAddUnavailable, setShowAddUnavailable] = useState(false);
  const [unavailableReason, setUnavailableReason] = useState("");
  
  // Email contacts
  const [emailContacts, setEmailContacts] = useState([]);
  
  // Staff Agency
  const [staffMembers, setStaffMembers] = useState([]);
  const [showAddStaffMember, setShowAddStaffMember] = useState(false);
  const [editingStaff, setEditingStaff] = useState(null);
  const [staffForm, setStaffForm] = useState({
    name: "", email: "", phone: "", role: "DJ", rate_per_event: "", notes: ""
  });
  
  // Event Staff Assignment
  const [showAssignStaff, setShowAssignStaff] = useState(false);
  const [selectedBookingForStaff, setSelectedBookingForStaff] = useState(null);
  const [staffSuggestions, setStaffSuggestions] = useState(null);
  const [eventStaffAssignments, setEventStaffAssignments] = useState([]);
  
  // Invoices
  const [invoices, setInvoices] = useState([]);

  // Manual Booking
  const [showAddBooking, setShowAddBooking] = useState(false);
  const [bookingForm, setBookingForm] = useState({
    customer_name: "", customer_email: "", customer_phone: "",
    event_date: "", event_type: "private", venue_address: "",
    package_id: "small", selected_addons: [], special_requests: "",
    deposit_paid: false, deposit_amount: 450, custom_total: ""
  });
  const [addonsData, setAddonsData] = useState(null);

  useEffect(() => {
    checkAuth();
  }, []);

  const getAuthHeaders = () => {
    const token = localStorage.getItem("adminToken");
    return { Authorization: `Bearer ${token}` };
  };

  const checkAuth = async () => {
    const token = localStorage.getItem("adminToken");
    if (!token) {
      navigate("/admin");
      return;
    }

    try {
      await axios.get(`${API}/admin/verify`, { headers: getAuthHeaders() });
      fetchData();
    } catch (error) {
      console.error("Auth check failed:", error);
      localStorage.removeItem("adminToken");
      localStorage.removeItem("adminEmail");
      navigate("/admin");
    }
  };

  const fetchData = async () => {
    try {
      const [statsRes, bookingsRes, datesRes, emailsRes, staffRes, invoicesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`, { headers: getAuthHeaders() }),
        axios.get(`${API}/bookings`, { headers: getAuthHeaders() }),
        axios.get(`${API}/unavailable-dates`, { headers: getAuthHeaders() }),
        axios.get(`${API}/email-contacts`, { headers: getAuthHeaders() }),
        axios.get(`${API}/staff-members`, { headers: getAuthHeaders() }),
        axios.get(`${API}/invoices`, { headers: getAuthHeaders() })
      ]);
      setStats(statsRes.data);
      setBookings(bookingsRes.data);
      setUnavailableDates(datesRes.data);
      setEmailContacts(emailsRes.data);
      setStaffMembers(staffRes.data);
      setInvoices(invoicesRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
      toast.error("Failed to load data");
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchData();
    setRefreshing(false);
    toast.success("Data refreshed");
  };

  const handleLogout = () => {
    localStorage.removeItem("adminToken");
    localStorage.removeItem("adminEmail");
    navigate("/admin");
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(
        `${API}/bookings/${bookingId}/status?status=${status}`,
        {},
        { headers: getAuthHeaders() }
      );
      toast.success(`Booking ${status}`);
      fetchData();
    } catch (error) {
      console.error("Error updating status:", error);
      toast.error("Failed to update booking");
    }
  };

  const deleteBooking = async (bookingId) => {
    if (!window.confirm("Are you sure you want to delete this booking?")) return;
    
    try {
      await axios.delete(`${API}/bookings/${bookingId}`, { headers: getAuthHeaders() });
      toast.success("Booking deleted");
      setSelectedBooking(null);
      fetchData();
    } catch (error) {
      console.error("Error deleting booking:", error);
      toast.error("Failed to delete booking");
    }
  };

  // Unavailable dates functions
  const addUnavailableDate = async () => {
    if (!selectedDate) return;
    
    try {
      await axios.post(`${API}/unavailable-dates`, {
        date: format(selectedDate, "yyyy-MM-dd"),
        reason: unavailableReason
      }, { headers: getAuthHeaders() });
      toast.success("Date marked unavailable");
      setShowAddUnavailable(false);
      setUnavailableReason("");
      fetchData();
    } catch (error) {
      toast.error("Failed to add unavailable date");
    }
  };

  const removeUnavailableDate = async (dateId) => {
    try {
      await axios.delete(`${API}/unavailable-dates/${dateId}`, { headers: getAuthHeaders() });
      toast.success("Date removed");
      fetchData();
    } catch (error) {
      toast.error("Failed to remove date");
    }
  };

  // Staff Agency functions
  const handleStaffFormSubmit = async () => {
    if (!staffForm.name || !staffForm.email || !staffForm.phone || !staffForm.rate_per_event) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      if (editingStaff) {
        await axios.put(`${API}/staff-members/${editingStaff.id}`, {
          ...staffForm,
          rate_per_event: parseFloat(staffForm.rate_per_event)
        }, { headers: getAuthHeaders() });
        toast.success("Staff member updated");
      } else {
        await axios.post(`${API}/staff-members`, {
          ...staffForm,
          rate_per_event: parseFloat(staffForm.rate_per_event)
        }, { headers: getAuthHeaders() });
        toast.success("Staff member added");
      }
      setShowAddStaffMember(false);
      setEditingStaff(null);
      setStaffForm({ name: "", email: "", phone: "", role: "DJ", rate_per_event: "", notes: "" });
      fetchData();
    } catch (error) {
      toast.error("Failed to save staff member");
    }
  };

  const deleteStaffMember = async (staffId) => {
    if (!window.confirm("Delete this staff member?")) return;
    try {
      await axios.delete(`${API}/staff-members/${staffId}`, { headers: getAuthHeaders() });
      toast.success("Staff member deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete staff member");
    }
  };

  const editStaffMember = (staff) => {
    setEditingStaff(staff);
    setStaffForm({
      name: staff.name,
      email: staff.email,
      phone: staff.phone,
      role: staff.role,
      rate_per_event: staff.rate_per_event.toString(),
      notes: staff.notes || ""
    });
    setShowAddStaffMember(true);
  };

  // Event Staff Assignment
  const openStaffAssignment = async (booking) => {
    setSelectedBookingForStaff(booking);
    try {
      const [suggestionsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/event-staff/suggestions/${booking.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/event-staff/${booking.id}`, { headers: getAuthHeaders() })
      ]);
      setStaffSuggestions(suggestionsRes.data);
      setEventStaffAssignments(assignmentsRes.data);
      setShowAssignStaff(true);
    } catch (error) {
      toast.error("Failed to load staff suggestions");
    }
  };

  const assignStaffToEvent = async (staffMemberId) => {
    try {
      await axios.post(`${API}/event-staff/assign?booking_id=${selectedBookingForStaff.id}&staff_member_id=${staffMemberId}`, {}, { headers: getAuthHeaders() });
      toast.success("Staff assigned and notified by email");
      // Refresh suggestions
      const [suggestionsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/event-staff/suggestions/${selectedBookingForStaff.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/event-staff/${selectedBookingForStaff.id}`, { headers: getAuthHeaders() })
      ]);
      setStaffSuggestions(suggestionsRes.data);
      setEventStaffAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to assign staff");
    }
  };

  const removeEventStaff = async (assignmentId) => {
    try {
      await axios.delete(`${API}/event-staff/${assignmentId}`, { headers: getAuthHeaders() });
      toast.success("Staff removed from event");
      const [suggestionsRes, assignmentsRes] = await Promise.all([
        axios.get(`${API}/event-staff/suggestions/${selectedBookingForStaff.id}`, { headers: getAuthHeaders() }),
        axios.get(`${API}/event-staff/${selectedBookingForStaff.id}`, { headers: getAuthHeaders() })
      ]);
      setStaffSuggestions(suggestionsRes.data);
      setEventStaffAssignments(assignmentsRes.data);
    } catch (error) {
      toast.error("Failed to remove staff");
    }
  };

  // Invoice functions
  const generateInvoice = (bookingId) => {
    window.open(`/invoice/${bookingId}`, '_blank');
  };

  const updateInvoiceStatus = async (invoiceId, status) => {
    try {
      await axios.patch(`${API}/invoices/${invoiceId}/status?status=${status}`, {}, { headers: getAuthHeaders() });
      toast.success("Invoice status updated");
      fetchData();
    } catch (error) {
      toast.error("Failed to update invoice");
    }
  };

  // Email contacts functions
  const deleteEmailContact = async (contactId) => {
    try {
      await axios.delete(`${API}/email-contacts/${contactId}`, { headers: getAuthHeaders() });
      toast.success("Contact deleted");
      fetchData();
    } catch (error) {
      toast.error("Failed to delete contact");
    }
  };

  // Manual Booking functions
  const PACKAGES_LIST = [
    { id: "small", name: "Small Package", price: 1000, guests: "0-50" },
    { id: "medium", name: "Medium Package", price: 1550, guests: "50-100" },
    { id: "large", name: "Large Package", price: 2100, guests: "100-150" },
    { id: "dj_only", name: "DJ & Soundsystem Only", price: 500, guests: "Any" }
  ];

  const ADDONS_LIST = [
    { id: "cocktails_small", label: "Cocktails (Small)", price: 500 },
    { id: "cocktails_medium", label: "Cocktails (Medium)", price: 1000 },
    { id: "cocktails_large", label: "Cocktails (Large)", price: 1500 },
    { id: "spirits_small", label: "Spirits & Mixers (Small)", price: 200 },
    { id: "spirits_medium", label: "Spirits & Mixers (Medium)", price: 400 },
    { id: "spirits_large", label: "Spirits & Mixers (Large)", price: 600 },
    { id: "wine_6", label: "Extra Wine (6 Bottles)", price: 140 },
    { id: "wine_12", label: "Extra Wine (12 Bottles)", price: 250 },
    { id: "keg_30", label: "Extra 30L Keg", price: 180 },
    { id: "keg_50", label: "Extra 50L Keg", price: 230 }
  ];

  const toggleAddon = (addonId) => {
    setBookingForm(prev => ({
      ...prev,
      selected_addons: prev.selected_addons.includes(addonId)
        ? prev.selected_addons.filter(id => id !== addonId)
        : [...prev.selected_addons, addonId]
    }));
  };

  const getCalculatedTotal = () => {
    const pkg = PACKAGES_LIST.find(p => p.id === bookingForm.package_id);
    const pkgPrice = pkg ? pkg.price : 0;
    const addonsTotal = bookingForm.selected_addons.reduce((sum, id) => {
      const addon = ADDONS_LIST.find(a => a.id === id);
      return sum + (addon ? addon.price : 0);
    }, 0);
    return pkgPrice + addonsTotal;
  };

  const handleManualBookingSubmit = async () => {
    if (!bookingForm.customer_name || !bookingForm.event_date) {
      toast.error("Please fill in customer name and event date");
      return;
    }

    try {
      const payload = {
        ...bookingForm,
        deposit_amount: parseInt(bookingForm.deposit_amount) || 450,
        custom_total: bookingForm.custom_total ? parseInt(bookingForm.custom_total) : null
      };
      await axios.post(`${API}/admin/bookings`, payload, { headers: getAuthHeaders() });
      toast.success("Event added successfully");
      setShowAddBooking(false);
      setBookingForm({
        customer_name: "", customer_email: "", customer_phone: "",
        event_date: "", event_type: "private", venue_address: "",
        package_id: "small", selected_addons: [], special_requests: "",
        deposit_paid: false, deposit_amount: 450, custom_total: ""
      });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to create event");
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case "confirmed":
        return <span className="badge-confirmed">Confirmed</span>;
      case "pending":
        return <span className="badge-pending">Pending</span>;
      case "cancelled":
        return <span className="badge-cancelled">Cancelled</span>;
      case "completed":
        return <span className="bg-blue-500/20 text-blue-400 px-3 py-1 text-xs uppercase tracking-wider font-semibold">Completed</span>;
      case "paid":
        return <span className="bg-green-500/20 text-green-400 px-3 py-1 text-xs uppercase tracking-wider font-semibold">Paid</span>;
      default:
        return <span className="badge-pending">{status}</span>;
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric"
    });
  };

  // Get booking for selected date
  const getBookingForDate = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.find(b => b.event_date === dateStr);
  };

  // Check if date is unavailable
  const isDateUnavailable = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return unavailableDates.some(d => d.date === dateStr);
  };

  // Check if date has booking
  const hasBooking = (date) => {
    const dateStr = format(date, "yyyy-MM-dd");
    return bookings.some(b => b.event_date === dateStr);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="spinner" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 h-full w-64 bg-[#0A0A0A] border-r border-white/10 p-6 flex flex-col">
        <div className="mb-8">
          <div className="text-lg font-black uppercase tracking-wider text-white">
            Charlesworth <span className="text-yellow-400">Club</span>
          </div>
          <p className="text-neutral-500 text-xs mt-1">Admin Dashboard</p>
        </div>
        
        <nav className="flex-grow">
          <div className="space-y-2">
            <button 
              onClick={() => setActiveTab("bookings")}
              className={`w-full flex items-center gap-3 px-4 py-3 font-semibold text-left text-sm ${
                activeTab === "bookings" ? 'bg-yellow-400/10 text-yellow-400' : 'text-white hover:bg-white/5'
              }`}
              data-testid="nav-bookings"
            >
              <Calendar size={18} />
              Bookings
            </button>
            <button 
              onClick={() => setActiveTab("calendar")}
              className={`w-full flex items-center gap-3 px-4 py-3 font-semibold text-left text-sm ${
                activeTab === "calendar" ? 'bg-yellow-400/10 text-yellow-400' : 'text-white hover:bg-white/5'
              }`}
              data-testid="nav-calendar"
            >
              <CalendarX size={18} />
              Calendar
            </button>
            <button 
              onClick={() => setActiveTab("staff")}
              className={`w-full flex items-center gap-3 px-4 py-3 font-semibold text-left text-sm ${
                activeTab === "staff" ? 'bg-yellow-400/10 text-yellow-400' : 'text-white hover:bg-white/5'
              }`}
              data-testid="nav-staff"
            >
              <Users size={18} />
              Staff Agency
            </button>
            <button 
              onClick={() => setActiveTab("invoices")}
              className={`w-full flex items-center gap-3 px-4 py-3 font-semibold text-left text-sm ${
                activeTab === "invoices" ? 'bg-yellow-400/10 text-yellow-400' : 'text-white hover:bg-white/5'
              }`}
              data-testid="nav-invoices"
            >
              <FileText size={18} />
              Invoices
            </button>
            <button 
              onClick={() => setActiveTab("contacts")}
              className={`w-full flex items-center gap-3 px-4 py-3 font-semibold text-left text-sm ${
                activeTab === "contacts" ? 'bg-yellow-400/10 text-yellow-400' : 'text-white hover:bg-white/5'
              }`}
              data-testid="nav-contacts"
            >
              <Mail size={18} />
              Email Contacts
            </button>
          </div>
        </nav>

        <div className="pt-6 border-t border-white/10">
          <p className="text-neutral-500 text-sm mb-2">Logged in as</p>
          <p className="text-white text-sm font-semibold mb-4 truncate">
            {localStorage.getItem("adminEmail")}
          </p>
          <button 
            onClick={handleLogout}
            className="flex items-center gap-2 text-neutral-400 hover:text-red-400 transition-colors text-sm"
            data-testid="logout-button"
          >
            <LogOut size={18} />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="ml-64 p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="heading-lg text-white">
              {activeTab === "bookings" && "Bookings"}
              {activeTab === "calendar" && "Calendar & Availability"}
              {activeTab === "staff" && "Staff Agency"}
              {activeTab === "invoices" && "Invoices"}
              {activeTab === "contacts" && "Email Contacts"}
            </h1>
          </div>
          <button 
            onClick={handleRefresh}
            disabled={refreshing}
            className="btn-secondary flex items-center gap-2 py-3"
            data-testid="refresh-button"
          >
            <RefreshCw size={18} className={refreshing ? "animate-spin" : ""} />
            Refresh
          </button>
        </div>

        {/* Stats Cards */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8" data-testid="stats-section">
            <div className="surface-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-yellow-400/10 flex items-center justify-center text-yellow-400">
                  <Calendar size={24} />
                </div>
                <div>
                  <p className="label-text">Total Bookings</p>
                  <p className="text-3xl font-black text-white">{stats.total_bookings}</p>
                </div>
              </div>
            </div>

            <div className="surface-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-green-500/10 flex items-center justify-center text-green-400">
                  <DollarSign size={24} />
                </div>
                <div>
                  <p className="label-text">Total Revenue</p>
                  <p className="text-3xl font-black text-white">£{stats.total_revenue?.toLocaleString()}</p>
                </div>
              </div>
            </div>

            <div className="surface-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-blue-500/10 flex items-center justify-center text-blue-400">
                  <Users size={24} />
                </div>
                <div>
                  <p className="label-text">Staff Members</p>
                  <p className="text-3xl font-black text-white">{staffMembers.length}</p>
                </div>
              </div>
            </div>

            <div className="surface-card p-6">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-purple-500/10 flex items-center justify-center text-purple-400">
                  <FileText size={24} />
                </div>
                <div>
                  <p className="label-text">Pending Invoices</p>
                  <p className="text-3xl font-black text-white">{invoices.filter(i => i.status === "pending").length}</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Bookings Tab */}
        {activeTab === "bookings" && (
          <div className="surface-card" data-testid="bookings-table-container">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h2 className="text-white font-bold text-lg">All Bookings</h2>
              <button
                onClick={() => setShowAddBooking(true)}
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="add-event-button"
              >
                <Plus size={18} />
                Add Event
              </button>
            </div>
            
            {bookings.length === 0 ? (
              <div className="p-12 text-center">
                <Calendar className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">No bookings yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table" data-testid="bookings-table">
                  <thead>
                    <tr>
                      <th>Customer</th>
                      <th>Event Date</th>
                      <th>Package</th>
                      <th>Total</th>
                      <th>Balance</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {bookings.map((booking) => (
                      <tr key={booking.id} data-testid={`booking-row-${booking.id}`}>
                        <td>
                          <div>
                            <p className="text-white font-semibold">{booking.customer_name}</p>
                            <p className="text-neutral-500 text-sm">{booking.customer_email}</p>
                          </div>
                        </td>
                        <td className="text-white">{formatDate(booking.event_date)}</td>
                        <td className="text-white">{booking.package_name}</td>
                        <td className="text-white font-bold">£{booking.total_price?.toLocaleString()}</td>
                        <td className="text-yellow-400 font-bold">£{(booking.total_price - booking.deposit_amount).toLocaleString()}</td>
                        <td>{getStatusBadge(booking.status)}</td>
                        <td>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button className="p-2 hover:bg-white/10 transition-colors">
                                <MoreVertical size={18} className="text-neutral-400" />
                              </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-[#1A1A1A] border-white/10">
                              <DropdownMenuItem 
                                onClick={() => setSelectedBooking(booking)}
                                className="text-white hover:bg-white/10 cursor-pointer"
                              >
                                <Eye size={16} className="mr-2" />
                                View Details
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openStaffAssignment(booking)}
                                className="text-blue-400 hover:bg-white/10 cursor-pointer"
                              >
                                <UserPlus size={16} className="mr-2" />
                                Assign Staff
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => generateInvoice(booking.id)}
                                className="text-yellow-400 hover:bg-white/10 cursor-pointer"
                              >
                                <FileText size={16} className="mr-2" />
                                Create Invoice
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => updateBookingStatus(booking.id, "completed")}
                                className="text-green-400 hover:bg-white/10 cursor-pointer"
                              >
                                <CheckCircle size={16} className="mr-2" />
                                Mark Completed
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => deleteBooking(booking.id)}
                                className="text-red-400 hover:bg-white/10 cursor-pointer"
                              >
                                <Trash2 size={16} className="mr-2" />
                                Delete
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Calendar Tab */}
        {activeTab === "calendar" && (
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            <div className="lg:col-span-2">
              <div className="surface-card p-6">
                <h3 className="text-white font-bold mb-4">Event Calendar</h3>
                <p className="text-neutral-400 text-sm mb-4">
                  <span className="inline-block w-3 h-3 bg-red-500 mr-2"></span> Unavailable
                  <span className="inline-block w-3 h-3 bg-green-500 ml-4 mr-2"></span> Booked
                </p>
                <CalendarComponent
                  mode="single"
                  selected={selectedDate}
                  onSelect={setSelectedDate}
                  className="bg-[#121212] w-full"
                  modifiers={{
                    unavailable: (date) => isDateUnavailable(date),
                    booked: (date) => hasBooking(date)
                  }}
                  modifiersStyles={{
                    unavailable: { backgroundColor: 'rgba(239, 68, 68, 0.3)', color: '#ef4444' },
                    booked: { backgroundColor: 'rgba(34, 197, 94, 0.3)', color: '#22c55e' }
                  }}
                />
              </div>
            </div>

            <div className="space-y-6">
              <div className="surface-card p-6">
                <h3 className="text-white font-bold mb-4">
                  {selectedDate ? format(selectedDate, "EEEE, MMMM d, yyyy") : "Select a Date"}
                </h3>
                
                {selectedDate && (
                  <div className="space-y-4">
                    {getBookingForDate(selectedDate) ? (
                      <div className="bg-green-500/10 border border-green-500/30 p-4">
                        <p className="text-green-400 font-bold mb-2">Booked Event</p>
                        <p className="text-white">{getBookingForDate(selectedDate).customer_name}</p>
                        <p className="text-neutral-400 text-sm">{getBookingForDate(selectedDate).package_name}</p>
                        <button
                          onClick={() => openStaffAssignment(getBookingForDate(selectedDate))}
                          className="text-yellow-400 text-sm mt-2 hover:underline"
                        >
                          Assign Staff
                        </button>
                      </div>
                    ) : isDateUnavailable(selectedDate) ? (
                      <div className="bg-red-500/10 border border-red-500/30 p-4">
                        <p className="text-red-400 font-bold">Unavailable</p>
                        <button
                          onClick={() => {
                            const dateRecord = unavailableDates.find(d => d.date === format(selectedDate, "yyyy-MM-dd"));
                            if (dateRecord) removeUnavailableDate(dateRecord.id);
                          }}
                          className="text-red-400 text-sm mt-2 hover:underline"
                        >
                          Remove Unavailable
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => setShowAddUnavailable(true)}
                        className="w-full btn-secondary text-sm py-2 flex items-center justify-center gap-2"
                      >
                        <CalendarX size={16} />
                        Mark as Unavailable
                      </button>
                    )}
                  </div>
                )}
              </div>

              <div className="surface-card p-6">
                <h3 className="text-white font-bold mb-4">Unavailable Dates</h3>
                {unavailableDates.length === 0 ? (
                  <p className="text-neutral-500 text-sm">No unavailable dates set</p>
                ) : (
                  <div className="space-y-2 max-h-48 overflow-y-auto">
                    {unavailableDates.map((date) => (
                      <div key={date.id} className="flex items-center justify-between bg-white/5 p-2">
                        <div>
                          <p className="text-white text-sm">{formatDate(date.date)}</p>
                          {date.reason && <p className="text-neutral-500 text-xs">{date.reason}</p>}
                        </div>
                        <button onClick={() => removeUnavailableDate(date.id)} className="text-red-400 hover:text-red-300">
                          <X size={16} />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Staff Agency Tab */}
        {activeTab === "staff" && (
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <p className="text-neutral-400">Manage your DJ and Bar staff agency</p>
              </div>
              <button
                onClick={() => {
                  setEditingStaff(null);
                  setStaffForm({ name: "", email: "", phone: "", role: "DJ", rate_per_event: "", notes: "" });
                  setShowAddStaffMember(true);
                }}
                className="btn-primary flex items-center gap-2"
              >
                <Plus size={18} />
                Add Staff Member
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {/* DJs */}
              <div className="surface-card p-6">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-purple-500/20 text-purple-400 flex items-center justify-center text-sm">DJ</span>
                  DJs
                </h3>
                {staffMembers.filter(s => s.role === "DJ").length === 0 ? (
                  <p className="text-neutral-500 text-sm">No DJs added yet</p>
                ) : (
                  <div className="space-y-3">
                    {staffMembers.filter(s => s.role === "DJ").map(staff => (
                      <div key={staff.id} className="bg-white/5 p-4 flex justify-between items-start">
                        <div>
                          <p className="text-white font-semibold">{staff.name}</p>
                          <p className="text-neutral-400 text-sm">{staff.email}</p>
                          <p className="text-neutral-400 text-sm">{staff.phone}</p>
                          <p className="text-yellow-400 font-bold mt-1">£{staff.rate_per_event}/event</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => editStaffMember(staff)} className="text-blue-400 hover:text-blue-300">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => deleteStaffMember(staff.id)} className="text-red-400 hover:text-red-300">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Bar Staff */}
              <div className="surface-card p-6">
                <h3 className="text-white font-bold mb-4 flex items-center gap-2">
                  <span className="w-8 h-8 bg-blue-500/20 text-blue-400 flex items-center justify-center text-sm">Bar</span>
                  Bar Staff
                </h3>
                {staffMembers.filter(s => s.role === "Bar").length === 0 ? (
                  <p className="text-neutral-500 text-sm">No bar staff added yet</p>
                ) : (
                  <div className="space-y-3">
                    {staffMembers.filter(s => s.role === "Bar").map(staff => (
                      <div key={staff.id} className="bg-white/5 p-4 flex justify-between items-start">
                        <div>
                          <p className="text-white font-semibold">{staff.name}</p>
                          <p className="text-neutral-400 text-sm">{staff.email}</p>
                          <p className="text-neutral-400 text-sm">{staff.phone}</p>
                          <p className="text-yellow-400 font-bold mt-1">£{staff.rate_per_event}/event</p>
                        </div>
                        <div className="flex gap-2">
                          <button onClick={() => editStaffMember(staff)} className="text-blue-400 hover:text-blue-300">
                            <Edit2 size={16} />
                          </button>
                          <button onClick={() => deleteStaffMember(staff.id)} className="text-red-400 hover:text-red-300">
                            <Trash2 size={16} />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Staff Requirements Info */}
            <div className="surface-card p-6">
              <h3 className="text-white font-bold mb-4">Auto-Assignment Rules</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="bg-white/5 p-4">
                  <p className="text-yellow-400 font-bold">Small Events (0-50)</p>
                  <p className="text-white">1 DJ</p>
                </div>
                <div className="bg-white/5 p-4">
                  <p className="text-yellow-400 font-bold">Medium Events (50-100)</p>
                  <p className="text-white">1 DJ + 1 Bar Staff</p>
                </div>
                <div className="bg-white/5 p-4">
                  <p className="text-yellow-400 font-bold">Large Events (100-150)</p>
                  <p className="text-white">1 DJ + 2 Bar Staff</p>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Invoices Tab */}
        {activeTab === "invoices" && (
          <div className="surface-card">
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <div>
                <h2 className="text-white font-bold text-lg">Invoices</h2>
                <p className="text-neutral-400 text-sm mt-1">Create, edit and export invoices</p>
              </div>
              <button
                onClick={() => window.open('/invoice', '_blank')}
                className="btn-primary flex items-center gap-2 text-sm"
                data-testid="create-invoice-button"
              >
                <Plus size={18} />
                Create Invoice
              </button>
            </div>
            
            {invoices.length === 0 ? (
              <div className="p-12 text-center">
                <FileText className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">No invoices yet</p>
                <p className="text-neutral-500 text-sm mt-2">Click "Create Invoice" to get started</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Invoice #</th>
                      <th>Customer</th>
                      <th>Event Date</th>
                      <th>Balance Due</th>
                      <th>Due Date</th>
                      <th>Status</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoices.map((invoice) => (
                      <tr key={invoice.id}>
                        <td className="text-yellow-400 font-mono">{invoice.invoice_number}</td>
                        <td>
                          <div>
                            <p className="text-white font-semibold">{invoice.customer_name}</p>
                            <p className="text-neutral-500 text-sm">{invoice.customer_email}</p>
                          </div>
                        </td>
                        <td className="text-white">{formatDate(invoice.event_date)}</td>
                        <td className="text-white font-bold">£{invoice.balance_due?.toLocaleString()}</td>
                        <td className="text-neutral-400">{formatDate(invoice.due_date)}</td>
                        <td>{getStatusBadge(invoice.status)}</td>
                        <td>
                          {invoice.status === "pending" ? (
                            <button
                              onClick={() => updateInvoiceStatus(invoice.id, "paid")}
                              className="text-green-400 hover:text-green-300 text-sm font-semibold"
                            >
                              Mark Paid
                            </button>
                          ) : (
                            <span className="text-green-400">✓ Paid</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* Contacts Tab */}
        {activeTab === "contacts" && (
          <div className="surface-card">
            <div className="p-6 border-b border-white/10">
              <h2 className="text-white font-bold text-lg">Email Contact List</h2>
              <p className="text-neutral-400 text-sm mt-1">All emails collected from bookings and contact forms</p>
            </div>
            
            {emailContacts.length === 0 ? (
              <div className="p-12 text-center">
                <Mail className="w-16 h-16 text-neutral-600 mx-auto mb-4" />
                <p className="text-neutral-400">No contacts yet</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="admin-table">
                  <thead>
                    <tr>
                      <th>Name</th>
                      <th>Email</th>
                      <th>Phone</th>
                      <th>Source</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailContacts.map((contact) => (
                      <tr key={contact.id}>
                        <td className="text-white font-semibold">{contact.name}</td>
                        <td className="text-white">{contact.email}</td>
                        <td className="text-neutral-400">{contact.phone || "-"}</td>
                        <td>
                          <span className={`px-2 py-1 text-xs uppercase ${
                            contact.source === "booking" ? "bg-green-500/20 text-green-400" : "bg-blue-500/20 text-blue-400"
                          }`}>
                            {contact.source}
                          </span>
                        </td>
                        <td>
                          <button onClick={() => deleteEmailContact(contact.id)} className="text-red-400 hover:text-red-300 p-2">
                            <Trash2 size={16} />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </main>

      {/* Booking Detail Modal */}
      {selectedBooking && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setSelectedBooking(null)}>
          <div className="bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <div className="p-6 border-b border-white/10 flex items-center justify-between">
              <h3 className="heading-md text-white">Booking Details</h3>
              <button onClick={() => setSelectedBooking(null)} className="text-neutral-400 hover:text-white">
                <XCircle size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              <div>
                <p className="label-text mb-3">Customer Information</p>
                <div className="surface-card p-4 space-y-2">
                  <div className="flex justify-between"><span className="text-neutral-400">Name</span><span className="text-white">{selectedBooking.customer_name}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Email</span><span className="text-white">{selectedBooking.customer_email}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Phone</span><span className="text-white">{selectedBooking.customer_phone}</span></div>
                </div>
              </div>

              <div>
                <p className="label-text mb-3">Event Details</p>
                <div className="surface-card p-4 space-y-2">
                  <div className="flex justify-between"><span className="text-neutral-400">Date</span><span className="text-white">{formatDate(selectedBooking.event_date)}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Type</span><span className="text-white capitalize">{selectedBooking.event_type}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Venue</span><span className="text-white">{selectedBooking.venue_address}</span></div>
                </div>
              </div>

              <div>
                <p className="label-text mb-3 flex items-center gap-2"><ShoppingCart size={16} />Shopping List</p>
                <div className="surface-card p-4 space-y-3">
                  <div className="border-b border-white/10 pb-3">
                    <p className="text-yellow-400 font-bold mb-2">{selectedBooking.package_name}</p>
                    <ul className="text-neutral-300 text-sm space-y-1">
                      {selectedBooking.package_id === "small" && (<><li>• 1x 30L Keg</li><li>• 12 Bottles of Wine</li></>)}
                      {selectedBooking.package_id === "medium" && (<><li>• 1x 50L Keg</li><li>• 24 Bottles of Wine</li></>)}
                      {selectedBooking.package_id === "large" && (<><li>• 1x 50L Keg</li><li>• 1x 30L Keg</li><li>• 36 Bottles of Wine</li></>)}
                      {selectedBooking.package_id === "dj_only" && (<><li>• Professional DJ</li><li>• Full PA System</li><li>• Up to 5 hours</li></>)}
                    </ul>
                  </div>
                  {selectedBooking.selected_addons?.length > 0 && (
                    <div>
                      <p className="text-neutral-500 text-sm mb-2">Add-ons:</p>
                      <ul className="text-neutral-300 text-sm space-y-1">
                        {selectedBooking.selected_addons.map((addon, idx) => (<li key={idx}>• {addon.label} (+£{addon.price})</li>))}
                      </ul>
                    </div>
                  )}
                </div>
              </div>

              <div>
                <p className="label-text mb-3">Pricing</p>
                <div className="surface-card p-4 space-y-3">
                  <div className="flex justify-between"><span className="text-white">Total</span><span className="text-white font-bold text-xl">£{selectedBooking.total_price?.toLocaleString()}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Deposit Paid</span><span className="text-green-400">£{selectedBooking.deposit_amount}</span></div>
                  <div className="flex justify-between"><span className="text-neutral-400">Balance Due</span><span className="text-yellow-400 font-bold">£{(selectedBooking.total_price - selectedBooking.deposit_amount).toLocaleString()}</span></div>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex gap-4">
              <button onClick={() => setSelectedBooking(null)} className="btn-secondary flex-1">Close</button>
              <button onClick={() => { openStaffAssignment(selectedBooking); setSelectedBooking(null); }} className="btn-primary flex-1">Assign Staff</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Staff Member Modal */}
      {showAddStaffMember && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowAddStaffMember(false)}>
          <div className="bg-[#121212] border border-white/10 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-4">{editingStaff ? "Edit Staff Member" : "Add Staff Member"}</h3>
            <div className="space-y-4">
              <input type="text" placeholder="Name *" value={staffForm.name} onChange={(e) => setStaffForm({...staffForm, name: e.target.value})} className="input-dark" />
              <input type="email" placeholder="Email *" value={staffForm.email} onChange={(e) => setStaffForm({...staffForm, email: e.target.value})} className="input-dark" />
              <input type="tel" placeholder="Phone *" value={staffForm.phone} onChange={(e) => setStaffForm({...staffForm, phone: e.target.value})} className="input-dark" />
              <select value={staffForm.role} onChange={(e) => setStaffForm({...staffForm, role: e.target.value})} className="input-dark">
                <option value="DJ">DJ</option>
                <option value="Bar">Bar Staff</option>
              </select>
              <input type="number" placeholder="Rate per event (£) *" value={staffForm.rate_per_event} onChange={(e) => setStaffForm({...staffForm, rate_per_event: e.target.value})} className="input-dark" />
              <textarea placeholder="Notes (optional)" value={staffForm.notes} onChange={(e) => setStaffForm({...staffForm, notes: e.target.value})} className="input-dark" rows={2} />
            </div>
            <div className="flex gap-4 mt-6">
              <button onClick={() => setShowAddStaffMember(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleStaffFormSubmit} className="btn-primary flex-1">{editingStaff ? "Update" : "Add"}</button>
            </div>
          </div>
        </div>
      )}

      {/* Assign Staff Modal */}
      {showAssignStaff && selectedBookingForStaff && staffSuggestions && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowAssignStaff(false)}>
          <div className="bg-[#121212] border border-white/10 p-6 w-full max-w-lg max-h-[80vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-2">Assign Staff</h3>
            <p className="text-neutral-400 text-sm mb-4">{selectedBookingForStaff.customer_name} - {formatDate(selectedBookingForStaff.event_date)}</p>
            
            <div className="bg-yellow-400/10 border border-yellow-400/30 p-4 mb-6">
              <p className="text-yellow-400 font-bold">Requirements for {selectedBookingForStaff.package_name}</p>
              <p className="text-white">{staffSuggestions.dj_needed} DJ{staffSuggestions.dj_needed > 1 ? 's' : ''} + {staffSuggestions.bar_needed} Bar Staff</p>
            </div>

            {/* Already Assigned */}
            {eventStaffAssignments.length > 0 && (
              <div className="mb-6">
                <p className="text-neutral-400 text-sm font-semibold uppercase tracking-wider mb-2">Assigned Staff</p>
                <div className="space-y-2">
                  {eventStaffAssignments.map(assignment => (
                    <div key={assignment.id} className="bg-green-500/10 border border-green-500/30 p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">{assignment.staff_name}</p>
                        <p className="text-neutral-400 text-sm">{assignment.staff_role} - £{assignment.rate}</p>
                        {assignment.email_sent && <p className="text-green-400 text-xs mt-1">✓ Email sent</p>}
                      </div>
                      <button onClick={() => removeEventStaff(assignment.id)} className="text-red-400 hover:text-red-300">
                        <X size={18} />
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Available DJs */}
            <div className="mb-4">
              <p className="text-neutral-400 text-sm font-semibold uppercase tracking-wider mb-2">Available DJs</p>
              {staffSuggestions.available_djs.length === 0 ? (
                <p className="text-neutral-500 text-sm">No DJs available</p>
              ) : (
                <div className="space-y-2">
                  {staffSuggestions.available_djs.map(dj => (
                    <div key={dj.id} className="bg-white/5 p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">{dj.name}</p>
                        <p className="text-yellow-400 text-sm">£{dj.rate_per_event}/event</p>
                      </div>
                      <button onClick={() => assignStaffToEvent(dj.id)} className="btn-primary text-sm py-2 px-4">
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Available Bar Staff */}
            <div className="mb-4">
              <p className="text-neutral-400 text-sm font-semibold uppercase tracking-wider mb-2">Available Bar Staff</p>
              {staffSuggestions.available_bar_staff.length === 0 ? (
                <p className="text-neutral-500 text-sm">No bar staff available</p>
              ) : (
                <div className="space-y-2">
                  {staffSuggestions.available_bar_staff.map(staff => (
                    <div key={staff.id} className="bg-white/5 p-3 flex justify-between items-center">
                      <div>
                        <p className="text-white font-semibold">{staff.name}</p>
                        <p className="text-yellow-400 text-sm">£{staff.rate_per_event}/event</p>
                      </div>
                      <button onClick={() => assignStaffToEvent(staff.id)} className="btn-primary text-sm py-2 px-4">
                        Assign
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button onClick={() => setShowAssignStaff(false)} className="btn-secondary w-full mt-4">Close</button>
          </div>
        </div>
      )}

      {/* Add Unavailable Date Modal */}
      {showAddUnavailable && selectedDate && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowAddUnavailable(false)}>
          <div className="bg-[#121212] border border-white/10 p-6 w-full max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-white font-bold mb-4">Mark Date Unavailable</h3>
            <p className="text-neutral-400 mb-4">{format(selectedDate, "EEEE, MMMM d, yyyy")}</p>
            <input type="text" value={unavailableReason} onChange={(e) => setUnavailableReason(e.target.value)} placeholder="Reason (optional)" className="input-dark mb-4" />
            <div className="flex gap-4">
              <button onClick={() => setShowAddUnavailable(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={addUnavailableDate} className="btn-primary flex-1">Confirm</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Manual Event/Booking Modal */}
      {showAddBooking && (
        <div className="fixed inset-0 bg-black/80 z-50 flex items-center justify-center p-6" onClick={() => setShowAddBooking(false)}>
          <div className="bg-[#121212] border border-white/10 w-full max-w-2xl max-h-[90vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="add-event-modal">
            <div className="p-6 border-b border-white/10 flex items-center justify-between sticky top-0 bg-[#121212] z-10">
              <div>
                <h3 className="text-white font-bold text-lg">Add Event Manually</h3>
                <p className="text-neutral-400 text-sm mt-1">For bookings that come in outside the website</p>
              </div>
              <button onClick={() => setShowAddBooking(false)} className="text-neutral-400 hover:text-white">
                <X size={24} />
              </button>
            </div>

            <div className="p-6 space-y-6">
              {/* Customer Details */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Customer Details</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="text" placeholder="Customer Name *" value={bookingForm.customer_name} onChange={(e) => setBookingForm({...bookingForm, customer_name: e.target.value})} className="input-dark" data-testid="manual-customer-name" />
                  <input type="email" placeholder="Email (optional)" value={bookingForm.customer_email} onChange={(e) => setBookingForm({...bookingForm, customer_email: e.target.value})} className="input-dark" data-testid="manual-customer-email" />
                  <input type="tel" placeholder="Phone (optional)" value={bookingForm.customer_phone} onChange={(e) => setBookingForm({...bookingForm, customer_phone: e.target.value})} className="input-dark" data-testid="manual-customer-phone" />
                </div>
              </div>

              {/* Event Details */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Event Details</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <input type="date" value={bookingForm.event_date} onChange={(e) => setBookingForm({...bookingForm, event_date: e.target.value})} className="input-dark" data-testid="manual-event-date" />
                  <select value={bookingForm.event_type} onChange={(e) => setBookingForm({...bookingForm, event_type: e.target.value})} className="input-dark" data-testid="manual-event-type">
                    <option value="private">Private Party</option>
                    <option value="wedding">Wedding</option>
                    <option value="corporate">Corporate</option>
                    <option value="birthday">Birthday</option>
                    <option value="festival">Festival</option>
                    <option value="other">Other</option>
                  </select>
                  <input type="text" placeholder="Venue Address" value={bookingForm.venue_address} onChange={(e) => setBookingForm({...bookingForm, venue_address: e.target.value})} className="input-dark md:col-span-2" data-testid="manual-venue-address" />
                </div>
              </div>

              {/* Package Selection */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Package</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  {PACKAGES_LIST.map(pkg => (
                    <button
                      key={pkg.id}
                      onClick={() => setBookingForm({...bookingForm, package_id: pkg.id})}
                      className={`p-4 text-left border transition-colors ${
                        bookingForm.package_id === pkg.id
                          ? 'border-yellow-400 bg-yellow-400/10'
                          : 'border-white/10 bg-white/5 hover:border-white/20'
                      }`}
                      data-testid={`manual-package-${pkg.id}`}
                    >
                      <p className="text-white font-semibold">{pkg.name}</p>
                      <p className="text-yellow-400 font-bold">£{pkg.price}</p>
                      <p className="text-neutral-500 text-xs">{pkg.guests} guests</p>
                    </button>
                  ))}
                </div>
              </div>

              {/* Add-ons */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Add-ons (optional)</p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto">
                  {ADDONS_LIST.map(addon => (
                    <label
                      key={addon.id}
                      className={`flex items-center gap-3 p-3 cursor-pointer border transition-colors ${
                        bookingForm.selected_addons.includes(addon.id)
                          ? 'border-yellow-400/50 bg-yellow-400/5'
                          : 'border-white/5 bg-white/5 hover:border-white/10'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={bookingForm.selected_addons.includes(addon.id)}
                        onChange={() => toggleAddon(addon.id)}
                        className="accent-yellow-400"
                      />
                      <div className="flex-1">
                        <p className="text-white text-sm">{addon.label}</p>
                      </div>
                      <p className="text-yellow-400 text-sm font-bold">£{addon.price}</p>
                    </label>
                  ))}
                </div>
              </div>

              {/* Special Requests */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Special Requests</p>
                <textarea
                  placeholder="Any notes or special requests..."
                  value={bookingForm.special_requests}
                  onChange={(e) => setBookingForm({...bookingForm, special_requests: e.target.value})}
                  className="input-dark"
                  rows={3}
                  data-testid="manual-special-requests"
                />
              </div>

              {/* Payment & Pricing */}
              <div>
                <p className="text-neutral-400 text-xs font-semibold uppercase tracking-wider mb-3">Payment</p>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={bookingForm.deposit_paid}
                        onChange={(e) => setBookingForm({...bookingForm, deposit_paid: e.target.checked})}
                        className="accent-yellow-400 w-5 h-5"
                        data-testid="manual-deposit-paid"
                      />
                      <span className="text-white">Deposit Paid</span>
                    </label>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="text-neutral-500 text-xs block mb-1">Deposit Amount (£)</label>
                      <input
                        type="number"
                        value={bookingForm.deposit_amount}
                        onChange={(e) => setBookingForm({...bookingForm, deposit_amount: e.target.value})}
                        className="input-dark"
                        data-testid="manual-deposit-amount"
                      />
                    </div>
                    <div>
                      <label className="text-neutral-500 text-xs block mb-1">Custom Total (£) - leave blank for auto</label>
                      <input
                        type="number"
                        value={bookingForm.custom_total}
                        onChange={(e) => setBookingForm({...bookingForm, custom_total: e.target.value})}
                        placeholder={getCalculatedTotal().toString()}
                        className="input-dark"
                        data-testid="manual-custom-total"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Price Summary */}
              <div className="bg-yellow-400/10 border border-yellow-400/30 p-4">
                <div className="flex justify-between mb-2">
                  <span className="text-neutral-400">Package</span>
                  <span className="text-white font-bold">£{PACKAGES_LIST.find(p => p.id === bookingForm.package_id)?.price || 0}</span>
                </div>
                {bookingForm.selected_addons.length > 0 && (
                  <div className="flex justify-between mb-2">
                    <span className="text-neutral-400">Add-ons ({bookingForm.selected_addons.length})</span>
                    <span className="text-white font-bold">
                      £{bookingForm.selected_addons.reduce((sum, id) => {
                        const addon = ADDONS_LIST.find(a => a.id === id);
                        return sum + (addon ? addon.price : 0);
                      }, 0)}
                    </span>
                  </div>
                )}
                <div className="flex justify-between pt-2 border-t border-yellow-400/30">
                  <span className="text-yellow-400 font-bold">Total</span>
                  <span className="text-yellow-400 font-bold text-xl">
                    £{bookingForm.custom_total || getCalculatedTotal()}
                  </span>
                </div>
              </div>
            </div>

            <div className="p-6 border-t border-white/10 flex gap-4 sticky bottom-0 bg-[#121212]">
              <button onClick={() => setShowAddBooking(false)} className="btn-secondary flex-1">Cancel</button>
              <button onClick={handleManualBookingSubmit} className="btn-primary flex-1" data-testid="manual-booking-submit">
                <Plus size={18} className="inline mr-2" />
                Add Event
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
